import { EasingFormulas } from "@dinahmoe/dmafjs-core";

const easingFormulas = new EasingFormulas();

let _groupIdCounter = 0;

class VisualTransformEngine {
  // Map<uid, Map<property, activeTransform>>
  _active = new Map();
  // Map<uid, Map<groupId, groupState>>
  _groups = new Map();
  _running = false;

  /**
   * Start transforms from a config. Accepts a single transform object or a
   * transform:group object (has a `transforms` array).
   *
   * @param {string} uid - node uid for tracking
   * @param {object} config - transform or transform:group data
   * @param {function} applyFn - called each frame with (property, currentValue)
   * @param {function} [completeFn] - called when a single transform completes
   * @param {function} [groupCompleteFn] - called when all transforms in a group complete
   */
  start(uid, config, applyFn, completeFn, groupCompleteFn) {
    if (config.transforms) {
      this._startGroup(uid, config, applyFn, completeFn, groupCompleteFn);
    } else if (config.property) {
      this._startSingle(uid, config, applyFn, completeFn);
    }
  }

  /**
   * Cancel all running transforms on a node.
   */
  cancelAll(uid) {
    this._active.delete(uid);
    this._groups.delete(uid);
  }

  /**
   * Returns true if the given node+property has an active transform.
   */
  isActive(uid, property) {
    return this._active.has(uid) && this._active.get(uid).has(property);
  }

  _startSingle(uid, config, applyFn, completeFn, groupId) {
    if (!this._active.has(uid)) {
      this._active.set(uid, new Map());
    }
    const propertyMap = this._active.get(uid);

    let valueStart = config.valueStart;

    if (propertyMap.has(config.property)) {
      const existing = propertyMap.get(config.property);
      // Compute current interpolated value of the existing transform
      if (valueStart === undefined) {
        const now = performance.now();
        if (existing.startTime !== null && now >= existing.startTime) {
          const elapsed = now - existing.startTime;
          const normalized = existing.duration === 0 ? 1 : Math.min(elapsed / existing.duration, 1);
          const easedProgress = existing.ease ? existing.ease(normalized, existing.curve) : normalized;
          valueStart = existing.valueStart + existing.range * easedProgress;
        } else {
          valueStart = existing.valueStart;
        }
      }
      propertyMap.delete(config.property);
    }

    const t = {
      property: config.property,
      valueStart,
      valueEnd: config.valueEnd,
      duration: config.duration ?? 0,
      delay: config.delay ?? 0,
      easing: config.easing,
      curve: config.curve,
      startTime: null,
      ease: null,
      range: null,
      applyFn,
      completeFn,
      groupId,
    };

    propertyMap.set(config.property, t);
    this._ensureRunning();
  }

  _startGroup(uid, config, applyFn, completeFn, groupCompleteFn) {
    const groupId = `grp_${++_groupIdCounter}`;
    const mode = config.mode || "parallel";
    const groupDelay = config.delay ?? 0;
    const entries = config.transforms;

    const groupState = {
      loop: config.loop === true,
      event: config.event,
      active: entries.length,
      entries,
      mode,
      applyFn,
      completeFn,
      groupCompleteFn,
      config,
    };

    if (!this._groups.has(uid)) {
      this._groups.set(uid, new Map());
    }
    this._groups.get(uid).set(groupId, groupState);

    let cumulativeDelay = groupDelay;

    for (let i = 0; i < entries.length; i++) {
      const entry = entries[i];
      let entryDelay;

      if (mode === "sequence") {
        entryDelay = cumulativeDelay;
        cumulativeDelay += (entry.delay ?? 0) + (entry.duration ?? 0);
      } else {
        // parallel
        entryDelay = groupDelay + (entry.delay ?? 0);
      }

      const entryConfig = { ...entry, delay: entryDelay };
      this._startSingle(uid, entryConfig, applyFn, completeFn, groupId);
    }
  }

  _handleCompletion(uid, t) {
    if (t.completeFn) {
      t.completeFn(t.property);
    }

    if (t.groupId) {
      const groupMap = this._groups.get(uid);
      if (!groupMap) return;
      const groupState = groupMap.get(t.groupId);
      if (!groupState) return;

      groupState.active--;

      if (groupState.active === 0) {
        if (groupState.loop) {
          // Restart all entries
          groupState.active = groupState.entries.length;
          const mode = groupState.mode;
          const groupDelay = groupState.config.delay ?? 0;
          let cumulativeDelay = groupDelay;

          for (let i = 0; i < groupState.entries.length; i++) {
            const entry = groupState.entries[i];
            let entryDelay;

            if (mode === "sequence") {
              entryDelay = cumulativeDelay;
              cumulativeDelay += (entry.delay ?? 0) + (entry.duration ?? 0);
            } else {
              entryDelay = groupDelay + (entry.delay ?? 0);
            }

            const entryConfig = { ...entry, delay: entryDelay };
            this._startSingle(uid, entryConfig, groupState.applyFn, groupState.completeFn, t.groupId);
          }
        } else {
          if (groupState.event && groupState.groupCompleteFn) {
            groupState.groupCompleteFn(groupState.event);
          }
          groupMap.delete(t.groupId);
          if (groupMap.size === 0) {
            this._groups.delete(uid);
          }
        }
      }
    }
  }

  _ensureRunning() {
    if (!this._running) {
      this._running = true;
      requestAnimationFrame(this._tick.bind(this));
    }
  }

  _tick() {
    const now = performance.now();

    for (const [uid, propertyMap] of this._active) {
      for (const [property, t] of propertyMap) {
        if (t.startTime === null) {
          t.startTime = now + (t.delay || 0);
          if (t.valueStart === undefined) {
            t.valueStart = 0;
          }
          t.ease = easingFormulas[t.easing]?.bind(easingFormulas) || easingFormulas.LINEAR.bind(easingFormulas);
          t.range = t.valueEnd - t.valueStart;
        }

        if (now < t.startTime) continue;

        const elapsed = now - t.startTime;
        const normalized = t.duration === 0 ? 1 : Math.min(elapsed / t.duration, 1);
        const easedProgress = t.ease(normalized, t.curve);
        const interpolated = t.valueStart + t.range * easedProgress;

        t.applyFn(property, interpolated);

        if (normalized === 1) {
          propertyMap.delete(property);
          if (propertyMap.size === 0) this._active.delete(uid);
          this._handleCompletion(uid, t);
        }
      }
    }

    if (this._active.size > 0) {
      requestAnimationFrame(this._tick.bind(this));
    } else {
      this._running = false;
    }
  }
}

const visualTransformEngine = new VisualTransformEngine();
export { VisualTransformEngine, visualTransformEngine };
